import axios from "axios";
import { store } from './../store';
import { LOGOUT, CLEAR_SEMESTER } from '../store/actions/types';
import { getAuthSession, getUserIdFromToken } from '../authSession';
import { isPublicAuthRoute, ROUTE_PATHS } from '../routes';

import { URL } from "../config";

// Express ETag revalidation returns 304; axios defaults reject anything outside 2xx.
axios.defaults.validateStatus = (status) =>
    (status >= 200 && status < 300) || status === 304;

// Last full GET body per user and URL. A 304 often arrives with an empty body;
// callers must still receive the representation the server just confirmed.
const revalidatedGetBodies = new Map();
let revalidationGeneration = 0;

const requestMethod = (config) => String(config?.method || 'get').toLowerCase();

const authorizationToken = (config) => {
    const headers = config?.headers;
    if (!headers) return '';
    const value = typeof headers.get === 'function'
        ? headers.get('Authorization')
        : (headers.Authorization || headers.authorization);
    return typeof value === 'string' ? value.replace(/^Bearer\s+/i, '') : '';
};

const revalidationCacheKey = (config) => {
    const userId = getUserIdFromToken(authorizationToken(config)) || 'anon';
    const url = config?.url || '';
    const params = config?.params && typeof config.params === 'object'
        ? JSON.stringify(
            Object.entries(config.params)
                .filter(([key]) => key !== '_')
                .sort(([left], [right]) => left.localeCompare(right))
        )
        : '';
    return `${userId} ${url} ${params}`;
};

const hasRevalidationBody = (data) => {
    if (data == null) return false;
    if (typeof data === 'string') return data.trim() !== '';
    return true;
};

const copyRevalidationBody = (data) => {
    if (!data || typeof data !== 'object') return data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) return data;
    try {
        return JSON.parse(JSON.stringify(data));
    } catch {
        return data;
    }
};

const rememberGetBody = (config, data) => {
    if (requestMethod(config) !== 'get' || !hasRevalidationBody(data)) return;
    // A GET that started before a save can finish afterwards with the old body.
    if (config.revalidationGeneration !== revalidationGeneration) return;
    revalidatedGetBodies.set(revalidationCacheKey(config), copyRevalidationBody(data));
};

const forgetUserGetBodies = () => {
    revalidationGeneration += 1;
    revalidatedGetBodies.clear();
};

// After a save, the browser can still answer the next GET with the pre-save
// list (a 304 plus the cached body, or a fresh cache hit). Skip that entry
// until a full response has been stored again.
const needsCacheBypass = (config) => {
    if (requestMethod(config) !== 'get' || config.__etagEmptyBodyRetry) return false;
    if (revalidationGeneration === 0) return false;
    return !revalidatedGetBodies.has(revalidationCacheKey(config));
};

const applyCacheBypass = (config) => {
    const token = String(revalidationGeneration);
    if (typeof URLSearchParams !== 'undefined' && config.params instanceof URLSearchParams) {
        const nextParams = new URLSearchParams(config.params);
        nextParams.set('_', token);
        config.params = nextParams;
        return;
    }
    config.params = { ...(config.params || {}), _: token };
};

const usersURL = URL + "/users";


const headers = {
    'Content-Type': 'multipart/form-data'
};

const getAuthToken = () => {
    const storedToken = localStorage.getItem('token');
    const profileToken = store.getState().user.profile?.token;

    // Prefer localStorage — it is updated synchronously on login/register,
    // while Redux can still hold the previous user's token briefly.
    const candidates = [storedToken, profileToken].filter(
        (token) => typeof token === 'string' && token.split('.').length === 3
    );

    return candidates[0] || null;
};

const shouldForceLogout = (error) => {
    const status = error?.response?.status;
    const backendMessage = (error?.response?.data?.error || '').toLowerCase();
    const runtimeMessage = (error?.message || '').toLowerCase();

    console.log('Checking if should force logout', { status, backendMessage, runtimeMessage });
        
    return (
        status === 401 ||
        (status === 400 && backendMessage.includes('invalid token')) ||
        backendMessage.includes('jwt expired') ||
        runtimeMessage.includes('jwt expired')
    );
};

const AuthService = {
    checkEmailExists: async (email) => {
        try {
            const response = await axios.post(`${usersURL}/check-email/`, {
                    email: email
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    register: async (userData) => {
        try {
            await axios.post(`${usersURL}/create/`, userData);

            // This part only runs on a successful (2xx) response
            const loginResponse = await axios.post(`${usersURL}/login/`, {
                email: userData.email,
                password: userData.password,
            });

            localStorage.setItem('token', loginResponse.data.token);

            return {
                data: {
                    status: "OK",
                    object: loginResponse.data
                },
                token: loginResponse.data.token
            };
        } catch (error) {
            console.error('Error during registration or login:', error.response?.data || error.message);
            throw error;
        }
    },
    login: async function (email, password) {
        //console.log('Attempting to log in with email:', email); // Log email received
        //console.log('REACT_APP_API_ENDPOINT:', process.env.REACT_APP_API_ENDPOINT);
        //console.log('POST URL:', `${usersURL}/login`);
        try {
            const response = await axios.post(`${usersURL}/login`, {
                email,
                password
            });
            //console.log('Login response:', response.data); // Log the response
            return response.data; // Return response.data directly
        } catch (error) {
            console.error('Login request failed:', error);
            throw error;
        }
    },
    update: function (object) {
        return axios.post(`${usersURL}/users/update/`, object);
    },
    getprofile: function () {
        return axios.get(`${usersURL}/profile/`);
    },
    getuserprofile: function (abortSignal, tokenOverride) {
        const token = tokenOverride || getAuthToken();
        if (!token) {
            throw new Error('No token found');
        }
        return axios.get(`${usersURL}/profile/`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal: abortSignal,
        });
    },
    uploadProfilePicture: async (userId, file, tokenOverride) => {
        const formData = new FormData();
        formData.append('profilePicture', file);
        const headers = { 'Content-Type': 'multipart/form-data' };
        const token = tokenOverride || getAuthToken();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const response = await axios.put(`${usersURL}/${userId}/profile-picture`, formData, {
            headers,
        });
        return response.data;
    },
    sendpasswordresetemail: function (email) {
        return axios.put(`${usersURL}/auth/reset/password/`, null, { params: { email } });
    },
    confirmemail: function (email, token) {
        return axios.get(`${usersURL}/auth/registration/confirm`, { params: { email, confirmationToken: token } });
    },
    recoverpassword: function (email, password, token) {
        return axios.put(`${usersURL}/auth/recover/confirm`, null, { params: { email, password, resetPasswordToken: token } });
    },
    uploaduserfile: function (file) {
        return axios.post(`${usersURL}/upload/user/cv/`, file, { headers });
    },
    getuserfile: function (userId) {
        return axios.get(`${usersURL}/files/user/`, { params: { userId }, responseType: 'blob' });
    },
    getuserselffile: function () {
        return axios.get(`${usersURL}/files/user/self/`, { responseType: 'blob' });
    },
    submitForApproval: function (userData) {
        return axios.post(`${usersURL}/submit/for/approval`, userData);
    },
};

axios.interceptors.request.use(
    async (request) => {
        if (!request.headers['Authorization']) {
            const token = getAuthToken();
            if (token) {
                request.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        request.authSessionId = getAuthSession();
        request.revalidationGeneration = revalidationGeneration;
        if (needsCacheBypass(request)) {
            applyCacheBypass(request);
        }
        return request;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    async (response) => {
        const method = requestMethod(response.config);

        if (method === 'get' && response.status === 304) {
            const requestGeneration = response.config?.revalidationGeneration;
            const cachedBody = revalidatedGetBodies.get(revalidationCacheKey(response.config));
            const cacheIsCurrent = cachedBody !== undefined
                && requestGeneration === revalidationGeneration;

            if (cacheIsCurrent) {
                // Keep the body stored from the last full response. A 304 can
                // also carry an older browser-cache copy of this URL.
                response.status = 200;
                response.data = copyRevalidationBody(cachedBody);
            } else if (!response.config.__etagEmptyBodyRetry) {
                const retryConfig = { ...response.config, __etagEmptyBodyRetry: true };
                retryConfig.headers = axios.AxiosHeaders.from(retryConfig.headers || {});
                retryConfig.headers.delete('Cache-Control');
                retryConfig.headers.delete('Pragma');
                delete retryConfig.revalidationGeneration;
                applyCacheBypass(retryConfig);
                return axios.request(retryConfig);
            } else if (hasRevalidationBody(response.data)) {
                response.status = 200;
                rememberGetBody(response.config, response.data);
            }
        } else if (method === 'get' && response.status >= 200 && response.status < 300) {
            rememberGetBody(response.config, response.data);
        } else if (
            response.status >= 200 &&
            response.status < 300 &&
            ['post', 'put', 'patch', 'delete'].includes(method)
        ) {
            // The next GET must not reuse a body from before this write.
            forgetUserGetBodies(response.config);
        }

        const refreshedToken = response.headers['x-refresh-token'];
        if (typeof refreshedToken === 'string' && refreshedToken.split('.').length === 3) {
            const currentToken = getAuthToken();
            const requestAuth = response.config?.headers?.Authorization;
            const requestSessionId = response.config?.authSessionId;

            // Ignore refresh tokens from stale in-flight requests (e.g. previous user after logout/login)
            if (
                currentToken &&
                requestAuth === `Bearer ${currentToken}` &&
                requestSessionId === getAuthSession()
            ) {
                localStorage.setItem('token', refreshedToken);
            }
        }
        return response;
    },
    (error) => {
        const isAuthRoute = error.config?.url?.includes('/users/login') || error.config?.url?.includes('/users/create');

        if (shouldForceLogout(error) && !isAuthRoute) {
            localStorage.removeItem('token');
            store.dispatch({ type: LOGOUT });
            store.dispatch({ type: CLEAR_SEMESTER });

            if (!isPublicAuthRoute()) {
                window.location.assign(ROUTE_PATHS.login);
            }
        }

        return Promise.reject(error);
    }
);

export default AuthService;
